import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Button, Col, Input, Row, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import EyeOutlineIcon from 'mdi-react/EyeOutlineIcon';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import CellphoneIcon from 'mdi-react/CellphoneIcon';
import GestureTapIcon from 'mdi-react/GestureTapIcon';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import DisplayCounter from '../../_uiComponents/DisplayCounter';
import Loading from '../../../shared/components/custom/Loading';
import { exportSheetsToExcelFile } from '../../../util/xlsx';

const p = partial('SponsorViews');

const RANGES = [3, 6, 12];
const PLACEMENTS = [
  'AppSponsor',
  'goalService',
  'matchBanner',
  'offer',
  'playerCard',
  'spotlightTile',
];

// Placements people can press; the others show no clicks ("–")
const CLICKABLE = ['offer', 'spotlightTile'];

const formatMonth = (month) => moment(`${month}-01`).format('MMM YYYY');
// Share of viewers who pressed, e.g. "3.2 %"
const clickRate = (clickers, viewers) =>
  viewers ? `${((100 * (clickers || 0)) / viewers).toFixed(1)} %` : '–';
const formatClicks = (row) =>
  row.clicks || CLICKABLE.includes(row.type)
    ? Number(row.clicks || 0).toLocaleString()
    : '–';
const formatNumber = (value) => Number(value || 0).toLocaleString();
const placementName = (type) =>
  PLACEMENTS.includes(type) ? p(`placement_${type}`) : type;
// The name the app sent; before apps sent names, the id from
// "spotlightTile-<id>/<companyId>"
const itemName = ({ entityName, entityId }) =>
  entityName ||
  `${entityId || ''}`.replace(/^[^-]+-/, '').replace(/\/[^/]*$/, '') ||
  '-';

// A company's sponsor views from the app: month by month, by placement and
// its most seen items. Without a company: every company of the club together,
// plus the companies seen most. Numbers come in nightly; past months are final.
const SponsorViewsCard = ({ companyID, companyName }) => {
  const isClub = !companyID;
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Company names for the club view
  const [companyNames, setCompanyNames] = useState({});

  useEffect(() => {
    if (!isClub) return;
    Api.companies
      .getClubCompanies()
      .then((companies) =>
        setCompanyNames(
          Object.fromEntries(
            (companies || []).map((company) => [company.id, company.name])
          )
        )
      )
      .catch(() => setCompanyNames({}));
  }, [isClub]);
  const nameOf = (id) => companyNames[id] || id;

  useEffect(() => {
    let cancelled = false;
    const to = moment().format('YYYY-MM');
    const from = moment()
      .subtract(months - 1, 'months')
      .format('YYYY-MM');
    setLoading(true);
    setFailed(false);
    (isClub
      ? Api.companies.sponsorViews.getClub({ from, to })
      : Api.companies.sponsorViews.getCompany(companyID, { from, to })
    )
      .then((result) => !cancelled && setData(result))
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [companyID, isClub, months]);

  const latest = data?.months?.[data.months.length - 1];

  // The tab's numbers as an Excel file, e.g. to send to the sponsor
  const exportToExcel = () => {
    exportSheetsToExcelFile(
      [
        {
          name: p('sheetMonths'),
          rows: data.months.map((row) => ({
            [p('month')]: formatMonth(row.month),
            [p('viewers')]: row.viewers,
            [p('sessions')]: row.sessions,
            [p('views')]: row.views,
            [p('clicks')]: row.clicks,
            [p('clickers')]: row.clickers,
            [p('clickRate')]: clickRate(row.clickers, row.viewers),
            [p('final')]: row.frozen ? p('yes') : p('no'),
          })),
        },
        {
          name: p('byPlacement'),
          rows: data.placements.map((row) => ({
            [p('placement')]: placementName(row.type),
            [p('sessions')]: row.sessions,
            [p('views')]: row.views,
            [p('clicks')]: formatClicks(row),
          })),
        },
        ...(isClub
          ? [
              {
                name: p('topCompanies'),
                rows: data.companies.map((row) => ({
                  [p('company')]: nameOf(row.companyId),
                  [p('sessions')]: row.sessions,
                  [p('views')]: row.views,
                  [p('clicks')]: row.clicks,
                })),
              },
            ]
          : []),
        {
          name: p('topItems'),
          rows: data.items.map((row) => ({
            ...(isClub && { [p('company')]: nameOf(row.companyId) }),
            [p('placement')]: placementName(row.type),
            [p('item')]: itemName(row),
            [p('sessions')]: row.sessions,
            [p('views')]: row.views,
            [p('clicks')]: formatClicks(row),
          })),
        },
      ],
      `${p('fileName')}-${isClub ? p('allCompanies') : companyName || companyID}-${data.from}-${data.to}`
    );
  };

  return (
    <div className="p-3">
      <Loading loading={loading} />
      <Row className="mb-3 align-items-end">
        <Col md={6}>
          <h4 className="mb-1">{isClub ? p('clubTitle') : p('title')}</h4>
          <small className="text-muted">{p('info')}</small>
          {data?.updatedAt && (
            <small className="text-muted d-block">
              {p('updatedAt', {
                date: moment(data.updatedAt).format('D MMM YYYY HH:mm'),
              })}
            </small>
          )}
        </Col>
        <Col md={2}>
          <Button
            size="sm"
            outline
            className="mb-0"
            disabled={!data?.months?.length}
            onClick={exportToExcel}
          >
            {p('export')}
          </Button>
        </Col>
        <Col md={4}>
          <Input
            type="select"
            bsSize="sm"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
          >
            {RANGES.map((count) => (
              <option key={count} value={count}>
                {p('lastMonths', { count })}
              </option>
            ))}
          </Input>
        </Col>
      </Row>

      {failed && <p className="text-danger">{p('loadFailed')}</p>}
      {data && !data.months.length && <p>{p('noData')}</p>}

      {data && data.months.length > 0 && (
        <>
          <div className="my-2">
            <DisplayCounter
              id="sponsor-viewers"
              title={p('viewers')}
              text={p('viewersIn', { month: formatMonth(latest.month) })}
              value={formatNumber(latest.viewers)}
              icon={<AccountMultipleIcon style={{ width: 18 }} />}
            />
            <DisplayCounter
              id="sponsor-sessions"
              title={p('sessions')}
              text={p('inPeriod')}
              value={formatNumber(data.totals.sessions)}
              icon={<CellphoneIcon style={{ width: 18 }} />}
            />
            <DisplayCounter
              id="sponsor-views"
              title={p('views')}
              text={p('inPeriod')}
              value={formatNumber(data.totals.views)}
              icon={<EyeOutlineIcon style={{ width: 18 }} />}
            />
            <DisplayCounter
              id="sponsor-clicks"
              title={p('clicks')}
              text={p('clickRateIn', {
                rate: clickRate(latest.clickers, latest.viewers),
                month: formatMonth(latest.month),
              })}
              value={formatNumber(data.totals.clicks)}
              icon={<GestureTapIcon style={{ width: 18 }} />}
            />
          </div>

          <div style={{ height: 300 }} className="my-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data.months}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e0e0e0"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tickFormatter={formatMonth}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#666', fontSize: 11 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#666', fontSize: 12 }}
                />
                {/* Clickers are far fewer than viewers: their own scale */}
                <YAxis
                  yAxisId="clicks"
                  orientation="right"
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#28a745', fontSize: 12 }}
                />
                <Tooltip
                  labelFormatter={formatMonth}
                  formatter={(value) => formatNumber(value)}
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="viewers"
                  name={p('viewers')}
                  stroke="#007bff"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  name={p('sessions')}
                  stroke="#000000"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="clickers"
                  yAxisId="clicks"
                  name={p('clickers')}
                  stroke="#28a745"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {latest && !latest.frozen && (
            <small className="text-muted d-block mb-3">
              {p('ongoingMonth', { month: formatMonth(latest.month) })}
            </small>
          )}

          {isClub && (
            <div className="mb-3">
              <h5>{p('topCompanies')}</h5>
              <Table responsive bordered size="sm">
                <thead>
                  <tr>
                    <th>{p('company')}</th>
                    <th>{p('sessions')}</th>
                    <th>{p('views')}</th>
                    <th>{p('clicks')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.companies.map((row) => (
                    <tr key={row.companyId}>
                      <td>
                        <Link to={`/companies/edit/${row.companyId}?tab=6`}>
                          {nameOf(row.companyId)}
                        </Link>
                      </td>
                      <td>{formatNumber(row.sessions)}</td>
                      <td>{formatNumber(row.views)}</td>
                      <td>{formatNumber(row.clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <Row>
            <Col lg={5} className="mb-3">
              <h5>{p('byPlacement')}</h5>
              <Table responsive bordered size="sm">
                <thead>
                  <tr>
                    <th>{p('placement')}</th>
                    <th>{p('sessions')}</th>
                    <th>{p('views')}</th>
                    <th>{p('clicks')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.placements.map((row) => (
                    <tr key={row.type}>
                      <td>{placementName(row.type)}</td>
                      <td>{formatNumber(row.sessions)}</td>
                      <td>{formatNumber(row.views)}</td>
                      <td>{formatClicks(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col lg={7} className="mb-3">
              <h5>{p('topItems')}</h5>
              <Table responsive bordered size="sm">
                <thead>
                  <tr>
                    {isClub && <th>{p('company')}</th>}
                    <th>{p('placement')}</th>
                    <th>{p('item')}</th>
                    <th>{p('sessions')}</th>
                    <th>{p('views')}</th>
                    <th>{p('clicks')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr key={`${row.companyId}-${row.type}-${row.entityId}`}>
                      {isClub && <td>{nameOf(row.companyId)}</td>}
                      <td>{placementName(row.type)}</td>
                      <td>{itemName(row)}</td>
                      <td>{formatNumber(row.sessions)}</td>
                      <td>{formatNumber(row.views)}</td>
                      <td>{formatClicks(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default SponsorViewsCard;
